/**
 * -----------------------------------------------------------------------------
 * Users Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the users of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useOrganizationUsersQuery,
  useResendInviteToOrgUserMutation,
} from "../../../api/network/adminApiServices";
import { useLoginAsUser } from "../../../hooks/loginAsUser";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { FilterType, OrganizationUser } from "../../../api/types/Admin";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useListingFilters } from "../../../hooks/listingFilter";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import AdminsUsersCreateNew from "./createNewUser";

const all_columns = [
  // "Sr. No",
  "User ID",
  "Description",
  "Role",
  "Contact Name",
  "Email",
  "Timezone",
  "Active",
  "Last Login",
  "Action",
];

const columnOrderingFields: Record<string, string> = {
  "User ID": "userid",
  Description: "description",
  Role: "roleid",
  "Contact Name": "contactname",
  Email: "contactemail",
  // Timezone: "", // intentionally excluded from sortability
  Active: "isactive",
  "Last Login": "lastlogintime",
  // "Action": "", // intentionally excluded from sortability
};

const ScreenDashboardAdminUsers = () => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.users" });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { users: orgUsersQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();
  const { canLoginAs, canLoginAsUser, loginAs } = useLoginAsUser();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Users);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns
          ? savedColumns.includes(colItem)
          : colItem === "Action" &&
              !canLoginAs &&
              getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) <
                ROLE_ACCESS_LEVELS.ALL
            ? false
            : true,
      };
    })
  );
  useEffect(() => {
    if (savedColumns) {
      const newColumns = all_columns.map((colItem) => {
        return {
          name: colItem,
          show:
            colItem === "Action" &&
            !canLoginAs &&
            getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) <
              ROLE_ACCESS_LEVELS.ALL
              ? false
              : savedColumns.includes(colItem),
        };
      });
      setColumns(newColumns);
    }
  }, [savedColumns]);

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  // filter mechainism
  const { filters } = useListingFilters();
  const [activeFilterSlug, setActiveFilterSlug] = React.useState<FilterType>(
    filters?.[0]?.slug
  );

  // updating query params on filter change
  useEffect(() => {
    const newParams = {
      ...listingQueryParams,
      users: {
        ...orgUsersQueryParams,
        isactive: undefined,
        page: 1,
        ...(activeFilterSlug === "both"
          ? {}
          : {
              isactive: activeFilterSlug === "active" ? 1 : 0,
            }),
      },
    };
    dispatch(setListingQueryParams(newParams));
  }, [activeFilterSlug]);

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        users: {
          ...orgUsersQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        users: {
          ...orgUsersQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // fetching user data
  const { data: dataOrgUsers, isFetching: isFetchingOrgUsers } =
    useOrganizationUsersQuery(
      {
        ...orgUsersQueryParams,
        // 'search_fields' intentionally not added here as default search fields are defined in the API service
      },
      {
        skip: !thisUserOrganizationId,
      }
    );
  const { count, next, previous, results } = dataOrgUsers || {};

  const [resendInviteAPITrigger, { isLoading: isLoadingResendInvite }] =
    useResendInviteToOrgUserMutation();
  const resendInviteHandler = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
    userInfo: TLegacyUserWithAccount
  ) => {
    e?.stopPropagation();
    e?.preventDefault();
    resendInviteAPITrigger({
      account_id: `${listingQueryParams.users.accountid}`,
      user_id: userInfo.userid,
      email: userInfo.contactemail,
      role_id: userInfo.roleid,
    })
      .unwrap()
      .then((data) => {
        if (
          !!data?.message &&
          data?.message?.toLowerCase()?.includes("successfully")
        ) {
          dispatch(setModalsData({ ...modalsState, showCreateUser: false }));
          toast.success(t("resend_invite.toast.success"));
        } else {
          toast.error(data?.message || t("resend_invite.toast.failed"));
        }
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("resend_invite.toast.failed")];
        toast.error(errors?.join(" "));
      });
  };

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgUsers]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as OrganizationUser[])).map(
        (item: TLegacyUserWithAccount, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.users}/${item.userid}`,
          cellData: [
            // (listingQueryParams.users.page_size * (listingQueryParams.users.page - 1)) + index + 1, // sr_no by page
            item?.userid, // id:
            item?.description || "-", // description:
            item?.roleid || "-", // role:
            item?.contactname || "-", // name:
            item?.contactemail, // email:
            item?.timezone || "-", // timezone:
            item?.isactive === 1 ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : item?.isactive === 0 ? (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ) : (
              "-"
            ), // active:
            !!item?.lastlogintime && !!tzForDateFns
              ? formattedDateTimeBasedOnTimezone(
                  item?.lastlogintime * 1000,
                  tzForDateFns
                )
              : "-", // last_login:
            ...[
              getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) >=
                ROLE_ACCESS_LEVELS.ALL || canLoginAs ? (
                <>
                  {canLoginAsUser(item?.userid, item?.isactive) && (
                    <AdminFormFieldSubmit
                      label={t("login_as_button")}
                      variant="primary"
                      onClick={(e) => loginAs(e, item.userid)}
                    />
                  )}
                  {
                    /* !!item?.lastlogintime - disabling this check and showing resend button for every user in order to allow admin to reset account for any user */
                    // false
                    getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) <
                    ROLE_ACCESS_LEVELS.ALL ? null : !item?.contactemail ? (
                      <span className="block text-[10px] leading-4 w-40 whitespace-break-spaces break-words text-heading-gray888">
                        {t("resend_invite_button_email_error")}
                      </span>
                    ) : (
                      <AdminFormFieldSubmit
                        label={t("resend_invite_button")}
                        variant="primary"
                        onClick={(e) => resendInviteHandler(e, item)}
                        disabled={isLoadingResendInvite}
                      />
                    )
                  }
                </>
              ) : null,
            ],
          ].filter((_, index) => columns[index].show),
          isActive: item?.isactive === 1,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(setModalsData({ ...modalsState, showCreateUser: true }));
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgUsersQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgUsersQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgUsersQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          filters={filters}
          activeFilterSlug={activeFilterSlug}
          handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Users}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgUsers}
            listingQueryParams={orgUsersQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgUsersQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgUsers && (
            <Pagination
              pageSize={orgUsersQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    users: {
                      ...orgUsersQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgUsersQueryParams.page_size) : 1
              }
              forcePage={orgUsersQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    users: { ...orgUsersQueryParams, page: data?.selected + 1 },
                  })
                );
              }}
              paginCurrentPage={orgUsersQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsUsersCreateNew />
    </>
  );
};

export default ScreenDashboardAdminUsers;
